import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWebsiteImages, slotBackground, slotUrl, slotList, quote } from '../lib/publicContent';
import { ed, edAbout, useAboutUs, useEditing, usePageMeta } from '../lib/editTexts';
import { CREST_SYMBOLS } from '../lib/editKeys';
import PublicNav from '../components/public/PublicNav';
import PublicFooter from '../components/public/PublicFooter';
import '../styles/public.css';

/*
 * Ueber uns = zentrale Wahrheit mit der App (AgoraHub → Inhalte → Über uns, Key about_us): Vision, Werte, Leitvers,
 * Leitung, Wappen-Punkte und Einleitung kommen aus about_us (`data-edit="about_us:<pfad>"`); der eingebaute Text gilt
 * nur, wenn das Feld dort leer ist. Nur Website-Texte (kleine Zeilen, Knoepfe) sind Uebersetzungs-Kennungen.
 */

// Lage der fuenf Wappen-Punkte um das Wappen (Reihenfolge wie CREST_SYMBOLS / about_us.logo_points).
const CREST_LAYOUT = [
  { box: { left: '50%', top: 0, transform: 'translateX(-50%)', width: 320, textAlign: 'center' }, line: { left: '50%', top: 54, width: 0, height: 22, borderLeft: '1px dashed #26211C', transform: 'translateX(-50%)' } },
  { box: { left: 0, top: 214, width: 236, textAlign: 'right' }, line: { left: 236, top: 236, width: 84, height: 0, borderTop: '1px dashed #26211C' } },
  { box: { right: 0, top: 214, width: 236, textAlign: 'left' }, line: { left: 680, top: 236, width: 84, height: 0, borderTop: '1px dashed #26211C' } },
  { box: { left: 0, top: 372, width: 236, textAlign: 'right' }, line: { left: 236, top: 394, width: 84, height: 0, borderTop: '1px dashed #26211C' } },
  { box: { right: 0, top: 372, width: 236, textAlign: 'left' }, line: { left: 680, top: 394, width: 84, height: 0, borderTop: '1px dashed #26211C' } },
];

const teaser = (paragraphs, length) => {
  const first = paragraphs[0] || '';
  return first.length > length ? `${first.slice(0, length)}…` : first;
};

export default function About() {
  const { t, i18n } = useTranslation();
  const about = useAboutUs();
  const slots = useWebsiteImages();
  const editing = useEditing();
  usePageMeta('about');
  const [profile, setProfile] = useState(null);
  const [vision, setVision] = useState(0);
  const toggle = which => setProfile(p => p === which ? null : which);
  // Im Bearbeitungsmodus ist alles aufgeklappt, damit jeder Text anklickbar ist.
  const isOpen = which => editing || profile === which;

  const visions = about.list('vision_cards');
  const values = about.list('values');
  const logoPoints = about.list('logo_points');
  const activeVision = visions[Math.min(vision, Math.max(visions.length - 1, 0))];
  const rami = about.link('leadership.global.pastor.cta');
  const pastorBio = about.paras('leadership.global.pastor.bio');
  const ladyBio = about.paras('leadership.global.first_lady.bio');
  const localBio = about.paras('leadership.local_leadership.pastor_bio');
  const bioStyle = last => ({ fontSize: 16, lineHeight: 1.7, color: '#5e554b', margin: last ? 0 : '0 0 16px' });
  const nameStyle = { fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 18, color: '#26211C', margin: '0 0 10px' };
  const toggleLabel = which => (
    <><span {...ed(isOpen(which) ? 'about.show_less' : 'about.show_more')}>{t(isOpen(which) ? 'about.show_less' : 'about.show_more')}</span>{isOpen(which) ? ' ↑' : ' ↓'}</>
  );
  const crestLogo = slotList(slots, 'brand.logo')[0];

  return (
    <div className="kac-public">
      <div className="kac-inner">
        <PublicNav />

        {/* HERO */}
        <div className="kac-hero">
          <div className="kp-kicker" style={{ marginBottom: 12 }} {...edAbout('hero.eyebrow')}>{about.text('hero.eyebrow')}</div>
          <h1 {...edAbout('hero.title')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 84, lineHeight: 0.95, margin: '0 0 28px', maxWidth: 960 }}>
            {about.text('hero.title')}
          </h1>
          <div {...edAbout('hero.paragraphs')} style={{ maxWidth: 660 }}>
            {about.paras('hero.paragraphs').map((text, i) => (
              <p key={i} style={{ fontSize: 19, lineHeight: 1.65, color: '#5e554b', margin: i ? '16px 0 0' : 0 }}>{text}</p>
            ))}
          </div>
        </div>

        {/* FULL-WIDTH IMAGE */}
        <div data-slot="page.about" style={{ height: 600, ...slotBackground(slots, 'page.about', 0, '/kac-assets/photos/DSC00096.jpg'), backgroundSize: 'cover', borderRadius: 6, margin: '0 0 72px' }} />

        {/* LEITUNG */}
        <div style={{ marginBottom: 80 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 24, marginBottom: 48 }}>
            <div className="kp-kicker" {...edAbout('leadership_story.title')}>{about.text('leadership_story.title')}</div>
            <h2 {...ed('about.leadership_heading')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 36, margin: 0 }}>{t('about.leadership_heading')}</h2>
          </div>

          {/* Global */}
          <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: 48, paddingBottom: 56, borderBottom: '1px solid rgba(120,100,70,0.22)' }}>
            <div data-slot="leadership.team" data-slot-index="0" style={{ height: 440, ...slotBackground(slots, 'leadership.team', 0, '/kac-assets/leadership/PastorRandolphWife.jpg', 'center top'), backgroundSize: 'cover', borderRadius: 6 }} />
            <div style={{ paddingTop: 8 }}>
              <div className="kp-kicker" style={{ marginBottom: 10 }} {...edAbout('leadership.global.title')}>{about.text('leadership.global.title')}</div>
              <h3 {...edAbout('leadership.global.subtitle')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 30, margin: '0 0 16px' }}>
                {about.text('leadership.global.subtitle')}
              </h3>
              <p {...edAbout('leadership.global.pastor.bio')} style={{ fontSize: 17, lineHeight: 1.65, color: '#5e554b', margin: '0 0 24px' }}>
                {teaser(pastorBio, 220)}
              </p>
              <button type="button" onClick={() => toggle('global')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 15, fontWeight: 600, color: '#C0291F' }}>
                {toggleLabel('global')}
              </button>
              {isOpen('global') && (
                <div style={{ marginTop: 28, display: 'grid', gridTemplateColumns: '120px 1fr', gap: 32 }}>
                  <img data-slot="leadership.team" data-slot-index="1" src={slotUrl(slots, 'leadership.team', 1, '/kac-assets/leadership/PastorRandolph.jpg')} alt={about.text('leadership.global.pastor.name')} style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover' }} />
                  <div>
                    <div {...edAbout('leadership.global.pastor.name')} style={nameStyle}>{about.text('leadership.global.pastor.name')}</div>
                    <div {...edAbout('leadership.global.pastor.bio')}>
                      {pastorBio.map((text, i) => <p key={i} style={bioStyle(i === pastorBio.length - 1)}>{text}</p>)}
                    </div>
                    {rami.url && (
                      <a href={rami.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginTop: 16, fontSize: 14, fontWeight: 600, color: '#C0291F' }}>{rami.label} →</a>
                    )}
                  </div>
                </div>
              )}
              {isOpen('global') && (
                <div style={{ marginTop: 28, display: 'grid', gridTemplateColumns: '120px 1fr', gap: 32 }}>
                  <img data-slot="leadership.team" data-slot-index="2" src={slotUrl(slots, 'leadership.team', 2, '/kac-assets/leadership/MamaPat.jpg')} alt={about.text('leadership.global.first_lady.name')} style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover' }} />
                  <div>
                    <div {...edAbout('leadership.global.first_lady.name')} style={nameStyle}>{about.text('leadership.global.first_lady.name')}</div>
                    <div {...edAbout('leadership.global.first_lady.bio')}>
                      {ladyBio.map((text, i) => <p key={i} style={bioStyle(i === ladyBio.length - 1)}>{text}</p>)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Berlin */}
          <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: 48, marginTop: 56, paddingTop: 0 }}>
            <div data-slot="leadership.lead" style={{ height: 440, ...slotBackground(slots, 'leadership.lead', 0, '/kac-assets/leadership/PastorDaniel.jpg', 'center top'), backgroundSize: 'cover', borderRadius: 6 }} />
            <div style={{ paddingTop: 8 }}>
              <div className="kp-kicker" style={{ marginBottom: 10 }} {...edAbout('leadership.local_leadership.title')}>{about.text('leadership.local_leadership.title')}</div>
              <h3 {...edAbout('leadership.local_leadership.subtitle')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 30, margin: '0 0 16px' }}>
                {about.text('leadership.local_leadership.subtitle')}
              </h3>
              <p {...edAbout('leadership.local_leadership.pastor_bio')} style={{ fontSize: 17, lineHeight: 1.65, color: '#5e554b', margin: '0 0 24px' }}>
                {teaser(localBio, 220)}
              </p>
              <button type="button" onClick={() => toggle('berlin')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 15, fontWeight: 600, color: '#C0291F' }}>
                {toggleLabel('berlin')}
              </button>
              {isOpen('berlin') && (
                <div style={{ marginTop: 28, display: 'grid', gridTemplateColumns: '120px 1fr', gap: 32 }}>
                  <img data-slot="leadership.lead" src={slotUrl(slots, 'leadership.lead', 0, '/kac-assets/leadership/PastorDaniel.jpg')} alt={about.text('leadership.local_leadership.subtitle')} style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover' }} />
                  <div {...edAbout('leadership.local_leadership.pastor_bio')}>
                    {localBio.map((text, i) => <p key={i} style={bioStyle(i === localBio.length - 1)}>{text}</p>)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* LEITVERS */}
        <div className="kac-red-panel" style={{ padding: '56px 64px', margin: '0 0 80px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.7, marginBottom: 24 }}>
            <span {...edAbout('key_scripture.title')}>{about.text('key_scripture.title')}</span> · <span {...edAbout('key_scripture.reference')}>{about.text('key_scripture.reference')}</span>
          </div>
          <blockquote {...edAbout('key_scripture.text')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 500, fontSize: 34, lineHeight: 1.35, margin: 0, maxWidth: 900 }}>
            {quote(i18n.language, about.text('key_scripture.text'))}
          </blockquote>
        </div>

        {/* VISION */}
        <div style={{ marginBottom: 80 }}>
          <div className="kp-kicker" style={{ marginBottom: 8 }} {...ed('about.vision_kicker')}>{t('about.vision_kicker')}</div>
          <h2 {...edAbout('sections.vision_title')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 40, margin: '0 0 40px' }}>{about.text('sections.vision_title')}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(visions.length, 1)},1fr)`, borderBottom: '1px solid rgba(120,100,70,0.2)' }}>
            {visions.map((position, i) => (
              <button key={position} type="button" onClick={() => setVision(i)} style={{
                display: 'flex', alignItems: 'center', gap: 16, padding: '24px 26px',
                cursor: 'pointer', background: 'none', border: 'none',
                borderBottom: activeVision === position ? '3px solid #C0291F' : '3px solid transparent',
                borderRight: i < visions.length - 1 ? '1px solid rgba(120,100,70,0.2)' : 'none',
                color: activeVision === position ? '#26211C' : '#857A6E',
                fontFamily: "'Hanken Grotesk',sans-serif", textAlign: 'left',
                transition: 'color .2s ease, border-color .2s ease',
              }}>
                <span style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 22, color: activeVision === position ? '#C0291F' : '#C2B6A4', flexShrink: 0 }}>{String(i + 1).padStart(2, '0')}</span>
                <span {...edAbout(`vision_cards.${position}.title`)} style={{ fontSize: 16, fontWeight: 600 }}>{about.text(`vision_cards.${position}.title`)}</span>
              </button>
            ))}
          </div>
          {(editing ? visions : [activeVision]).filter(position => position !== undefined).map(position => (
            <p key={position} {...edAbout(`vision_cards.${position}.text`)} style={{ fontSize: 20, lineHeight: 1.65, color: '#5e554b', maxWidth: 840, margin: '32px 0 0' }}>
              {about.text(`vision_cards.${position}.text`)}
            </p>
          ))}
        </div>

        {/* WERTE */}
        <div style={{ marginBottom: 80 }}>
          <div className="kp-kicker" style={{ marginBottom: 8 }} {...ed('about.values_kicker')}>{t('about.values_kicker')}</div>
          <h2 {...edAbout('sections.values_title')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 40, margin: '0 0 40px' }}>{about.text('sections.values_title')}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(values.length, 1)},1fr)`, gap: 0 }}>
            {values.map((position, i) => (
              <div key={position} style={{ padding: '32px 28px', borderRight: i < values.length - 1 ? '1px solid rgba(120,100,70,0.22)' : 'none' }}>
                <div style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 20, color: '#C0291F', marginBottom: 16 }}>{String(i + 1).padStart(2, '0')}</div>
                <div {...edAbout(`values.${position}.title`)} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 22, marginBottom: 10 }}>{about.text(`values.${position}.title`)}</div>
                <div {...edAbout(`values.${position}.description`)} style={{ fontSize: 14, color: '#857A6E', lineHeight: 1.5 }}>{about.text(`values.${position}.description`)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* WAPPEN */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 18, borderBottom: '2px solid #26211C', paddingBottom: 18, marginBottom: 40 }}>
            <span className="kp-kicker" {...edAbout('sections.logo_title')}>{about.text('sections.logo_title')}</span>
            <h2 {...ed('about.crest_heading')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 42, letterSpacing: '-0.02em', margin: 0 }}>{t('about.crest_heading')}</h2>
          </div>
          <div style={{ position: 'relative', height: 680, maxWidth: 1000, margin: '0 auto' }}>
            <img data-slot="brand.logo" src={crestLogo ? crestLogo.url : '/kac-assets/KAC-Badge.png'} alt={(crestLogo && crestLogo.alt) || t('about.crest_alt')} style={{ position: 'absolute', left: '50%', top: 80, transform: 'translateX(-50%)', height: 486, width: 'auto', display: 'block' }} />

            {CREST_LAYOUT.map((layout, i) => {
              const position = logoPoints[i] !== undefined ? logoPoints[i] : i;
              return (
                <React.Fragment key={CREST_SYMBOLS[i]}>
                  <div style={{ position: 'absolute', ...layout.box }}>
                    <h3 {...edAbout(`logo_points.${position}.text`)} style={{ margin: '0 0 4px', fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 600, fontSize: 18 }}>{about.text(`logo_points.${position}.text`)}</h3>
                    <p {...ed(`about.crest.${CREST_SYMBOLS[i]}`)} style={{ margin: 0, fontSize: 13, lineHeight: 1.4, color: '#857A6E' }}>{t(`about.crest.${CREST_SYMBOLS[i]}`)}</p>
                  </div>
                  <div style={{ position: 'absolute', ...layout.line }} />
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <PublicFooter />
      </div>
    </div>
  );
}
